import { types } from 'mobx-state-tree';
import { AsyncApi } from '@icecreamsocial/mst-models';
import { IcsV1, V2 } from '@icecreamsocial/api-resources';

/**
 * @description Class representing a simple builder for creating API requests
 * @property {*} action an MST action from a Communicator-type MST
 * @property {*} [args] An object that specifies url keys and their values (not query params)
 * @property {*} [params] An object that specifies query param keys and their values
 * @property {*} [headers] An object that specifies header keys for a request and their values
 * @property {*} [options] An object that specifies AsyncApi options
 */
class RequestBuilder {
  /**
   * Create a request builder
   * @param {*} CommunicatorAction
   */
  constructor(CommunicatorAction) {
    this.action = CommunicatorAction;
    this.baseUrl = undefined;
    this.args = {};
    this.params = {};
    this.headers = {};
    this.options = {};
  }
  /**
   * A factory method for creating a request builder
   * @param {*} CommunicatorAction
   * @returns {RequestBuilder}
   */


  static request(CommunicatorAction) {
    return new RequestBuilder(CommunicatorAction);
  }

  call(CommunicatorAction = this.action, baseUrl = this.baseUrl) {
    const request = {
      args: this.args,
      params: this.params,
      headers: this.headers
    };

    if (baseUrl) {
      request.args.baseUrl = baseUrl;
    }

    return CommunicatorAction(request, this.options);
  }

  withArgs(args) {
    this.args = { ...this.args,
      ...args
    };
    return this;
  }

  withAuthorization(key) {
    this.headers = { ...this.headers,
      Authorization: `Bearer ${key}`
    };
    return this;
  }

  withBaseUrl(url) {
    this.args.baseUrl = url;
    return this;
  }

  withHeaders(headers) {
    this.headers = { ...this.headers,
      ...headers
    };
    return this;
  }

  withOptions(options) {
    this.options = { ...this.options,
      ...options
    };
    return this;
  }

  withParams(params) {
    this.params = { ...this.params,
      ...params
    };
    return this;
  }

}

const CommunicationStore = types.model({}).named('Communicators').views(self => ({
  createRequest(CommunicatorAction) {
    return RequestBuilder.request(CommunicatorAction);
  }

}));

// @ts-nocheck
/**
 * @note Communicator Node
 * This model is an interface for external communication with the ICS Affiliates API
 */

const ICSAffiliatesResources = AsyncApi.named('ICSAffiliatesResources').actions(self => ({
  getAffiliatesByLevel(request, options) {
    return self.createAsyncApi(IcsV1.AffiliateV1.getAffiliatesByLevel)(request, options);
  },

  getAffiliateById(request, options) {
    return self.createAsyncApi(IcsV1.AffiliateV1.getAffiliateById)(request, options);
  },

  createAffiliate(request, options) {
    return self.createAsyncApi(IcsV1.AffiliateV1.createAffiliate)(request, options);
  },

  updateAffiliate(request, options) {
    return self.createAsyncApi(IcsV1.AffiliateV1.updateAffiliate)(request, options);
  },

  deleteAffiliate(request, options) {
    return self.createAsyncApi(IcsV1.AffiliateV1.deleteAffiliate)(request, options);
  }

}));

// @ts-nocheck
/**
 * @note Communicator Node
 * This model is an interface for external communication with the ICS API-Keys API
 */

const ICSApiKeysResources = AsyncApi.named('ICSApiKeysResources').actions(self => ({
  getApiKeys(request, options) {
    return self.createAsyncApi(IcsV1.ApiKeysV1.getApiKeys)(request, options);
  },

  createApiKey(request, options) {
    return self.createAsyncApi(IcsV1.ApiKeysV1.createApiKey)(request, options);
  },

  deleteApiKey(request, options) {
    return self.createAsyncApi(IcsV1.ApiKeysV1.deleteApiKey)(request, options);
  }

}));

// @ts-nocheck
/**
 * @note Communicator Node
 * This model is an interface for external communication with the ICS Auth API
 */

const ICSAuthResources = AsyncApi.named('ICSAuthResources').actions(self => ({
  auth(request, options) {
    return self.createAsyncApi(IcsV1.AccountCreationV1.loginToAccount)(request, options);
  }

}));

// @ts-nocheck
/**
 * @note Communicator Node
 * This model is an interface for external communication with the ICS Campaigns API
 */

const ICSCampaignsResources = AsyncApi.named('ICSCampaignsResources').actions(self => ({
  getCampaignsByLevel(request, options) {
    const maxAge = -1;
    return self.createAsyncApi(IcsV1.CampaignsV1.getCampaignsByLevel, maxAge)(request, options);
  },

  getCampaigns(request, options) {
    return self.createAsyncApi(IcsV1.CampaignsV1.getCampaigns)(request, options);
  },

  getCampaignById(request, options) {
    return self.createAsyncApi(IcsV1.CampaignsV1.getCampaignById)(request, options);
  },

  createCampaign(request, options) {
    const maxAge = -1;
    return self.createAsyncApi(IcsV1.CampaignsV1.createCampaign, maxAge)(request, options);
  },

  updateCampaign(request, options) {
    const maxAge = -1;
    return self.createAsyncApi(IcsV1.CampaignsV1.updateCampaign, maxAge)(request, options);
  },

  deleteCampaign(request, options) {
    return self.createAsyncApi(IcsV1.CampaignsV1.deleteCampaign)(request, options);
  },

  checkInfluencerProcessing(request, options) {
    return self.createAsyncApi(IcsV1.CampaignsV1.checkInfluencerProcessing)(request, options);
  },

  getGatewayStatus(request, options) {
    return self.createAsyncApi(IcsV1.CampaignsV1.checkForValidGateway)(request, options);
  },

  getCampaignEcommerceInjectableScript(request, options) {
    return self.createAsyncApi(IcsV1.CampaignsV1.getCampaignEcommerceInjectableScript)(request, options);
  },

  getCampaignConversions(request, options) {
    return self.createAsyncApi(IcsV1.CampaignsV1.getCampaignConversions)(request, options);
  },

  exportCampaignConversions(request, options) {
    return self.createAsyncApi(IcsV1.CampaignsV1.exportConversions)(request, options);
  }

}));

// @ts-nocheck
/**
 * @note Communicator Node
 * This model is an interface for external communication with the ICS Clients API
 */

const ICSClientsResources = AsyncApi.named('ICSClientsResources').actions(self => ({
  getClientsByLevel(request, options) {
    return self.createAsyncApi(IcsV1.ClientV1.getClientByLevel)(request, options);
  },

  getClientById(request, options) {
    return self.createAsyncApi(IcsV1.ClientV1.getClientById)(request, options);
  },

  createClient(request, options) {
    return self.createAsyncApi(IcsV1.ClientV1.createClient)(request, options);
  },

  updateClient(request, options) {
    return self.createAsyncApi(IcsV1.ClientV1.updateClient)(request, options);
  },

  deleteClient(request, options) {
    return self.createAsyncApi(IcsV1.ClientV1.deleteClient)(request, options);
  },

  unsubscribe(request, options) {
    return self.createAsyncApi(IcsV1.ClientV1.unsubscribe)(request, options);
  },

  resubscribe(request, options) {
    return self.createAsyncApi(IcsV1.ClientV1.resubscribe)(request, options);
  }

}));

// @ts-nocheck
/**
 * @note Communicator Node
 * This model is an interface for external communication with the ICS Companies API
 */

const ICSCompaniesResources = AsyncApi.named('ICSCompaniesResources').actions(self => ({
  getCompany(request, options) {
    return self.createAsyncApi(IcsV1.CompanyV1.getCompany)(request, options);
  },

  updateCompany(request, options) {
    const maxAge = -1;
    return self.createAsyncApi(IcsV1.CompanyV1.editCompany, maxAge)(request, options);
  }

}));

// @ts-nocheck
/**
 * @note Communicator Node
 * This model is an interface for external communication with the ICS "Client" API
 */

const ICSCustomersResources = AsyncApi.named('ICSCustomersResources').actions(self => ({
  createInfluencer2(request, options) {
    return self.createAsyncApi(IcsV1.CustomerV1.createInfluencer2)(request, options);
  },

  createIntermidiaryConversion(request, options) {
    return self.createAsyncApi(IcsV1.CustomerV1.createIntermidiaryConversion)(request, options);
  }

}));

// @ts-nocheck
/**
 * @note Communicator Node
 * This model is an interface for external communication with the ICS Affiliates API
 */

const ICSInfluencersResources = AsyncApi.named('ICSInfluencersResources').actions(self => ({
  getInfluencers(request, options) {
    return self.createAsyncApi(IcsV1.InfluencersV1.getInfluencers)(request, options);
  },

  getInfluencerById(request, options) {
    return self.createAsyncApi(IcsV1.InfluencersV1.getInfluencerById)(request, options);
  },

  exportInfluencers(request, options) {
    return self.createAsyncApi(IcsV1.InfluencersV1.exportInfluencers)(request, options);
  },

  createInfluencer(request, options) {
    return self.createAsyncApi(IcsV1.InfluencersV1.createInfluencer)(request, options);
  },

  updateInfluencer(request, options) {
    return self.createAsyncApi(IcsV1.InfluencersV1.updateInfluencer)(request, options);
  },

  deleteInfluencer(request, options) {
    return self.createAsyncApi(IcsV1.InfluencersV1.deleteInfluencer)(request, options);
  },

  getInvitesByInfluencerId(request, options) {
    return self.createAsyncApi(IcsV1.InfluencersV1.getInvitesByInfluencerId)(request, options);
  },

  getConversionByInfluencerId(request, options) {
    return self.createAsyncApi(IcsV1.InfluencersV1.getConversionByInfluencerId)(request, options);
  },

  getInviteByInfluencerAndInviteId(request, options) {
    return self.createAsyncApi(IcsV1.InfluencersV1.getInviteByInfluencerAndInviteId)(request, options);
  },

  getConversionsByInfluencerAndInviteId(request, options) {
    return self.createAsyncApi(IcsV1.InfluencersV1.getConversionsByInfluencerAndInviteId)(request, options);
  },

  getInfluencedConversionByInvitedInfluencerId(request, options) {
    return self.createAsyncApi(IcsV1.InfluencersV1.getInfluencedConversionByInvitedInfluencerId)(request, options);
  },

  deleteConversion(request, options) {
    return self.createAsyncApi(IcsV1.InfluencersV1.deleteConversion)(request, options);
  },

  postCliqReward(request, options) {
    return self.createAsyncApi(IcsV1.InfluencersV1.issueCliqRefundReward)(request, options);
  },

  postInfluencerProcessing(request, options) {
    return self.createAsyncApi(IcsV1.InfluencersV1.processInfluencer)(request, options);
  }

}));

// @ts-nocheck
/**
 * @note Communicator Node
 * This model is an interface for external communication with the ICS Invites API
 */

const ICSInvitesResources$1 = AsyncApi.named('ICSInvitesResources').actions(self => ({
  getCampaignInvites(request, options) {
    return self.createAsyncApi(IcsV1.InvitesV1.getInvitesOfCampaign)(request, options);
  },

  updateInvite(request, options) {
    return self.createAsyncApi(IcsV1.InvitesV1.updateInvite)(request, options);
  },

  exportInvites(request, options) {
    return self.createAsyncApi(IcsV1.InvitesV1.exportInvites)(request, options);
  }

}));

// @ts-nocheck
/**
 * @note Communicator Node
 * This model is an interface for external communication with the ICS Stats API
 */

const ICSUsersResources$1 = AsyncApi.named('ICSUsersResources').actions(self => ({
  getDailyStats(request, options) {
    return self.createAsyncApi(IcsV1.UsersV1.dailyStats)(request, options);
  },

  getStatisticsOverview(request, options) {
    return self.createAsyncApi(IcsV1.UsersV1.statisticsOverview)(request, options);
  }

}));

// @ts-nocheck
/**
 * @note Communicator Node
 * This model is an interface for external communication with the ICS Invites API
 */

const ICSInvitesResources = AsyncApi.named('ICSInvitesResources').actions(self => ({
  createProcessor(request, options) {
    return self.createAsyncApi(IcsV1.ProcessorsV1.createProcessor)(request, options);
  },

  deleteProcessor(request, options) {
    return self.createAsyncApi(IcsV1.ProcessorsV1.deleteProcessor)(request, options);
  },

  getProcessors(request, options) {
    return self.createAsyncApi(IcsV1.ProcessorsV1.getProcessors)(request, options);
  },

  getProcessorById(request, options) {
    return self.createAsyncApi(IcsV1.ProcessorsV1.getProcessorById)(request, options);
  },

  getSquareProcessorLocations(request, options) {
    return self.createAsyncApi(IcsV1.ProcessorsV1.getSquareProcessorLocations)(request, options);
  },

  revokeSquareAccessToken(request, options) {
    return self.createAsyncApi(IcsV1.ProcessorsV1.revokeSquareAccessToken)(request, options);
  },

  updateProcessor(request, options) {
    return self.createAsyncApi(IcsV1.ProcessorsV1.updateProcessor)(request, options);
  }

}));

// @ts-nocheck
/**
 * @note Communicator Node
 * This model is an interface for external communication with the ICS Users API
 */

const ICSUsersResources = AsyncApi.named('ICSUsersResources').actions(self => ({
  generateStateParam(request, options) {
    return self.createAsyncApi(IcsV1.UsersV1.generateStateParam)(request, options);
  },

  getUsersByLevel(request, options = {}) {
    const maxAge = options.maxAge;
    return self.createAsyncApi(IcsV1.UsersV1.getUsersByLevel, maxAge)(request, options);
  },

  getUserById(request, options) {
    return self.createAsyncApi(IcsV1.UsersV1.getUserById)(request, options);
  },

  createUser(request, options) {
    return self.createAsyncApi(IcsV1.UsersV1.createUser)(request, options);
  },

  updateUser(request, options) {
    const maxAge = options.maxAge;
    return self.createAsyncApi(IcsV1.UsersV1.updateUser, maxAge)(request, options);
  },

  deleteUser(request, options) {
    return self.createAsyncApi(IcsV1.UsersV1.deleteUser)(request, options);
  }

}));

/**
 * @note Communicator Node
 * This model is an interface for external communication with ICS Affiliate API V2
 */

const AffiliatesResources = AsyncApi.named('AffiliatesResources').actions(self => ({
  createAnAffiliate(request, options) {
    const maxAge = -1;
    return self.createAsyncApi(V2.Affiliates.createAnAffiliate, maxAge)(request, options);
  },

  updateAnAffiliate(request, options) {
    const maxAge = -1;
    return self.createAsyncApi(V2.Affiliates.updateAnAffiliate, maxAge)(request, options);
  },

  deleteAnAffiliate(request, options) {
    const maxAge = -1;
    return self.createAsyncApi(V2.Affiliates.deleteAnAffiliate, maxAge)(request, options);
  },

  getAffiliateById(request, options) {
    return self.createAsyncApi(V2.Affiliates.getAffiliateById)(request, options);
  },

  getNestedAffiliatesByAffiliateId(request, options) {
    return self.createAsyncApi(V2.Affiliates.getNestedAffiliatesByAffiliateId)(request, options);
  }

}));

// @ts-nocheck
/**
 * @note Communicator Node
 * This model is an interface for external communication with the ICS Auth API V2
 */

const AuthResources = AsyncApi.named('AuthResources').actions(self => ({
  auth(request, options) {
    return self.createAsyncApi(V2.Auth.getAuthorizationToken)(request, options);
  }

}));

// @ts-nocheck
/**
 * @note Communicator Node
 * This model is an interface for external communication with the ICS Campaigns API V2
 */

const CampaignsResources = AsyncApi.named('CampaignsResources').actions(self => ({
  refundGatewayVerification(request, options) {
    return self.createAsyncApi(V2.Campaigns.refundGatewayVerification)(request, options);
  },

  getIcsScriptByIntegrationPlatformMethod(request, options) {
    return self.createAsyncApi(V2.Campaigns.getIcsScriptByIntegrationPlatformMethod)(request, options);
  },

  // invites?
  getPaginatedInvites(request, options) {
    return self.createAsyncApi(V2.Campaigns.getPaginatedInvites)(request, options);
  },

  exportInvites(request, options) {
    return self.createAsyncApi(V2.Campaigns.exportInvites)(request, options);
  },

  getInvite(request, options) {
    return self.createAsyncApi(V2.Campaigns.getInvite)(request, options);
  },

  updateInvite(request, options) {
    return self.createAsyncApi(V2.Campaigns.updateInvite)(request, options);
  },

  getInvitesByInfluencerId(request, options) {
    return self.createAsyncApi(V2.Campaigns.getInvitesByInfluencerId)(request, options);
  },

  // conversions?
  createManualConversion(request, options) {
    return self.createAsyncApi(V2.Campaigns.createManualConversion)(request, options);
  },

  getConversionsOfAnInvite(request, options) {
    return self.createAsyncApi(V2.Campaigns.getConversionsOfAnInvite)(request, options);
  },

  getInfluencedConversionsByInvitedInfluencerId(request, options) {
    return self.createAsyncApi(V2.Campaigns.getInfluencedConversionsByInvitedInfluencerId)(request, options);
  },

  getConversionsByInfluencerId(request, options) {
    return self.createAsyncApi(V2.Campaigns.getConversionsByInfluencerId)(request, options);
  },

  cancelConversion(request, options) {
    return self.createAsyncApi(V2.Campaigns.cancelConversion)(request, options);
  },

  deleteAConversion(request, options) {
    return self.createAsyncApi(V2.Campaigns.deleteAConversion)(request, options);
  },

  getConversionsOfACampaign(request, options) {
    return self.createAsyncApi(V2.Campaigns.getConversionsOfACampaign)(request, options);
  },

  exportConversions(request, options) {
    return self.createAsyncApi(V2.Campaigns.exportConversions)(request, options);
  },

  getPaginatedConversions(request, options) {
    return self.createAsyncApi(V2.Campaigns.getPaginatedConversions)(request, options);
  },

  // campaign influencers ?
  getInfluencerById(request, options) {
    return self.createAsyncApi(V2.Campaigns.getInfluencerById)(request, options);
  },

  anonymizeInfluencer(request, options) {
    return self.createAsyncApi(V2.Campaigns.anonymizeInfluencer)(request, options);
  },

  updateInfluencer(request, options) {
    return self.createAsyncApi(V2.Campaigns.updateInfluencer)(request, options);
  },

  deleteInfluencer(request, options) {
    return self.createAsyncApi(V2.Campaigns.deleteInfluencer)(request, options);
  },

  processInfluencer(request, options) {
    return self.createAsyncApi(V2.Campaigns.processInfluencer)(request, options);
  },

  createInfluencer(request, options) {
    return self.createAsyncApi(V2.Campaigns.createInfluencer)(request, options);
  },

  getInfluencers(request, options) {
    return self.createAsyncApi(V2.Campaigns.getInfluencers)(request, options);
  },

  exportInfluencers(request, options) {
    return self.createAsyncApi(V2.Campaigns.exportInfluencers)(request, options);
  },

  showInfluencerRewardInformation(request, options) {
    return self.createAsyncApi(V2.Campaigns.showInfluencerRewardInformation)(request, options);
  },

  // campaign
  getCampaignById(request, options) {
    return self.createAsyncApi(V2.Campaigns.getCampaignById)(request, options);
  },

  updateCampaign(request, options) {
    const maxAge = -1;
    return self.createAsyncApi(V2.Campaigns.updateCampaign, maxAge)(request, options);
  },

  deleteCampaign(request, options) {
    return self.createAsyncApi(V2.Campaigns.deleteCampaign)(request, options);
  },

  createCampaign(request, options) {
    const maxAge = -1;
    return self.createAsyncApi(V2.Campaigns.createCampaign, maxAge)(request, options);
  },

  getCampaignByFilter(request, options) {
    return self.createAsyncApi(V2.Campaigns.getCampaignByFilter)(request, options);
  },

  unsubscribe(request, options) {
    return self.createAsyncApi(V2.Campaigns.unsubscribe)(request, options);
  },

  resubscribe(request, options) {
    return self.createAsyncApi(V2.Campaigns.resubscribe)(request, options);
  }

}));

// @ts-nocheck
/**
 * @note Communicator Node
 * This model is an interface for external communication with the ICS Client API V2
 */

const ClientResources = AsyncApi.named('ClientResources').actions(self => ({
  createGenericInvite(request, options) {
    return self.createAsyncApi(V2.Client.createGenericInvite)(request, options);
  },

  createInfluencer(request, options) {
    return self.createAsyncApi(V2.Client.createInfluencer)(request, options);
  },

  getCampaignInformation(request, options) {
    return self.createAsyncApi(V2.Client.getCampaignInformation)(request, options);
  },

  sendEmailToInfluencer(request, options) {
    return self.createAsyncApi(V2.Client.sendEmailToInfluencer)(request, options);
  },

  createFacebookInvite(request, options) {
    return self.createAsyncApi(V2.Client.createFacebookInvite)(request, options);
  },

  createEmailInvite(request, options) {
    return self.createAsyncApi(V2.Client.createEmailInvite)(request, options);
  },

  createInfluencerFromClientIntermediaryConversion(request, options) {
    return self.createAsyncApi(V2.Client.createInfluencerFromClientIntermediaryConversion)(request, options);
  },

  createInviteHashAndCookies(request, options) {
    return self.createAsyncApi(V2.Client.createInviteHashAndCookies)(request, options);
  },

  unsubscribe(request, options) {
    return self.createAsyncApi(V2.Client.unsubscribe)(request, options);
  },

  resubscribe(request, options) {
    return self.createAsyncApi(V2.Client.resubscribe)(request, options);
  }

}));

// @ts-nocheck
/**
 * @note Communicator Node
 * This model is an interface for external communication with the ICS Clients API V2
 */

const ClientsResources = AsyncApi.named('ClientsResources').actions(self => ({
  getClientById(request, options) {
    return self.createAsyncApi(V2.Clients.getClientById)(request, options);
  },

  deleteClient(request, options) {
    return self.createAsyncApi(V2.Clients.deleteClient)(request, options);
  },

  updateClient(request, options) {
    return self.createAsyncApi(V2.Clients.updateClient)(request, options);
  },

  createAClient(request, options) {
    return self.createAsyncApi(V2.Clients.createAClient)(request, options);
  },

  getNestedClients(request, options) {
    return self.createAsyncApi(V2.Clients.getNestedClients)(request, options);
  },

  regeneratePublicKey(request, options) {
    return self.createAsyncApi(V2.Clients.regeneratePublicKey)(request, options);
  }

}));

// @ts-nocheck
/**
 * @note Communicator Node
 * This model is an interface for external communication with the ICS Public API V2
 */

const PublicResources = AsyncApi.named('PublicResources').actions(self => ({
  createInfluencer(request, options) {
    return self.createAsyncApi(V2.Public.createInfluencer)(request, options);
  },

  getCampaign(request, options) {
    return self.createAsyncApi(V2.Public.getCampaign)(request, options);
  },

  emailInfluencer(request, options) {
    return self.createAsyncApi(V2.Public.emailInfluencer)(request, options);
  },

  trackConversion(request, options) {
    return self.createAsyncApi(V2.Public.trackConversion)(request, options);
  }

}));

// @ts-nocheck
/**
 * @note Communicator Node
 * This model is an interface for external communication with the ICS Users API V2
 */

const UsersResources = AsyncApi.named('UsersResources').actions(self => ({
  getASingleUserInformationByUserId(request, options) {
    return self.createAsyncApi(V2.Users.getASingleUserInformationByUserId)(request, options);
  },

  updatesAUser(request, options) {
    return self.createAsyncApi(V2.Users.updatesAUser)(request, options);
  },

  getAdminUsers(request, options) {
    return self.createAsyncApi(V2.Users.getAdminUsers)(request, options);
  },

  createsAdminUser(request, options) {
    return self.createAsyncApi(V2.Users.createsAdminUser)(request, options);
  },

  getNestedAdminUsersByAffiliateId(request, options) {
    return self.createAsyncApi(V2.Users.getNestedAdminUsersByAffiliateId)(request, options);
  },

  getCampaignOverviewStatistics(request, options) {
    return self.createAsyncApi(V2.Users.getCampaignOverviewStatistics)(request, options);
  },

  getDailyCampaignStatistics(request, options) {
    return self.createAsyncApi(V2.Users.getDailyCampaignStatistics)(request, options);
  },

  generateUserStateParam(request, options) {
    return self.createAsyncApi(V2.Users.generateUserStateParam)(request, options);
  }

}));

// @ts-nocheck
/**
 * @note Communicator Node
 * This model is an interface for external communication with the ICS Webhooks API V2
 */

const WebhooksResources = AsyncApi.named('WebhooksResources').actions(self => ({
  registerCampaignToWebhook(request, options) {
    return self.createAsyncApi(V2.Webhooks.registerCampaignToWebhook)(request, options);
  },

  getRegisteredCampaignWebhooks(request, options) {
    return self.createAsyncApi(V2.Webhooks.getRegisteredCampaignWebhooks)(request, options);
  },

  removeWebhookSubscription(request, options) {
    return self.createAsyncApi(V2.Webhooks.removeWebhookSubscription)(request, options);
  },

  getWebhookEvents(request, options) {
    return self.createAsyncApi(V2.Webhooks.getWebhookEvents)(request, options);
  }

}));

/**
 * @note Communicator Node
 * This model is an interface for external communication with ICS Company API V2
 */

const CompanyResources = AsyncApi.named('CompanyResources').actions(self => ({
  getCompanyInformation(request, options) {
    return self.createAsyncApi(V2.Company.getCompanyInformation)(request, options);
  },

  updateCompanyInformation(request, options) {
    return self.createAsyncApi(V2.Company.updateCompanyInformation)(request, options);
  },

  bulkUpdateCompanyCampaigns(request, options) {
    return self.createAsyncApi(V2.Company.bulkUpdateCompanyCampaigns)(request, options);
  }

}));

/**
 * @note Communicator Node
 * This model is an interface for external communication with the ICS Processors API V2
 */

const ProcessorsResources = AsyncApi.named('ProcessorsResources').actions(self => ({
  getProcessorInformationById(request, options) {
    return self.createAsyncApi(V2.Processors.getProcessorInformationById)(request, options);
  },

  getAllAccessibleProcessors(request, options) {
    return self.createAsyncApi(V2.Processors.getAllAccessibleProcessors)(request, options);
  },

  getSquareLocationsOfTheSquareProcessor(request, options) {
    return self.createAsyncApi(V2.Processors.getSquareLocationsOfTheSquareProcessor)(request, options);
  },

  createProcessor(request, options) {
    return self.createAsyncApi(V2.Processors.createProcessor)(request, options);
  },

  updateProcessor(request, options) {
    return self.createAsyncApi(V2.Processors.updateProcessor)(request, options);
  },

  deleteProcessor(request, options) {
    return self.createAsyncApi(V2.Processors.deleteProcessor)(request, options);
  },

  updateProcessorSquareToken(request, options) {
    return self.createAsyncApi(V2.Processors.updateProcessorSquareToken)(request, options);
  },

  revokeStripeToken(request, options) {
    return self.createAsyncApi(V2.Processors.revokeStripeToken)(request, options);
  }

}));

var index = /*#__PURE__*/Object.freeze({
	__proto__: null,
	ICSAffiliatesAPIV2: AffiliatesResources,
	ICSAuthAPIV2: AuthResources,
	ICSCampaignsAPIV2: CampaignsResources,
	ICSClientAPIV2: ClientResources,
	ICSClientsAPIV2: ClientsResources,
	ICSPublicAPIV2: PublicResources,
	ICSUsersAPIV2: UsersResources,
	ICSWebhooksAPIV2: WebhooksResources,
	ICSCompanyAPIV2: CompanyResources,
	ICSProcessorsAPIV2: ProcessorsResources
});

export { CommunicationStore as BaseCommunicator, ICSAffiliatesResources as ICSAffiliatesAPI, ICSApiKeysResources as ICSApiKeysAPI, ICSAuthResources as ICSAuthAPI, ICSCampaignsResources as ICSCampaignsAPI, ICSClientsResources as ICSClientsAPI, ICSCompaniesResources as ICSCompaniesAPI, ICSCustomersResources as ICSCustomersAPI, ICSInfluencersResources as ICSInfluencersAPI, ICSInvitesResources$1 as ICSInvitesAPI, ICSInvitesResources as ICSProcessorsAPI, ICSUsersResources$1 as ICSStatsAPI, ICSUsersResources as ICSUsersAPI, index as ICSV2 };
